import {
  CheckboxField,
  Disclosure,
  Field,
  Input,
  Pill,
  Row,
  Rows,
  Section,
  Select,
} from "@/components/kit";
import type { BindingKind, MasterDefinition } from "@/lib/api/types";
import { emptyMaster, toCode } from "@/lib/builder";

import { Fields, Names } from "./Fields";

/**
 * What a sport keeps, and — for the levels of its competition tree — where each
 * one is allowed to sit.
 *
 * The three level rules are the difference between a tree that permits anything
 * and one that can guide. Cricket holds a tour, a league season, a World Cup and
 * a one-off match, all legal; without these nothing could tell them from
 * nonsense, and "add inside" had nothing to offer.
 */

/**
 * What a thing a sport keeps can *be*.
 *
 * Short labels, because this is a dropdown in a three-column row and "Someone who
 * takes part" ran off the end of it. The `note` carries the meaning instead — it
 * had been sitting here unused, and it is better placed under the field than
 * crammed into the option.
 */
const KINDS: { value: BindingKind; label: string; note: string }[] = [
  { value: "participant", label: "Someone taking part", note: "Teams, players, horses, umpires" },
  { value: "competition-node", label: "A level", note: "Tour, season, stage, meeting, fight card" },
  { value: "venue", label: "A place", note: "Grounds, courses, pools, arenas" },
  { value: "outcome-codes", label: "A word list", note: "Ways of getting out, the going, statuses" },
];

const PARTY_TYPES = ["TEAM", "PERSON", "ANIMAL", "PAIR", "CREW", "ORGANISATION"];

export function MastersSection({
  bare = false,
  masters,
  onChange,
}: {
  masters: MasterDefinition[];
  onChange: (masters: MasterDefinition[]) => void;
  /** Render the contents without the section's own heading and rule.
   *  The wizard shows one section per step and supplies the heading itself, so
   *  the section's would be a second copy of it on the same screen. */
  bare?: boolean;
}) {
  const replace = (i: number, patch: Partial<MasterDefinition>) =>
    onChange(masters.map((m, j) => (j === i ? { ...m, ...patch } : m)));

  const levels = masters.filter((m) => m.binding.kind === "competition-node");

  const contents = (
    <>
      <Rows
        empty="Nothing yet. A sport keeps teams, players, venues, officials, its own lists of words — whatever it actually has. Each one becomes a screen."
        count={masters.length}
        addLabel="Add a record type"
        onAdd={() => onChange([...masters, emptyMaster("participant", "")])}
        onMove={(from, to) => {
          const next = [...masters];
          const [moved] = next.splice(from, 1);
          next.splice(to, 0, moved);
          onChange(next);
        }}
        onRemove={(i) => onChange(masters.filter((_, j) => j !== i))}
      >
        {masters.map((master, i) => {
          const kind = master.binding.kind;
          return (
            <Row
              key={i}
              index={i}
              badge={<Pill>{master.code || "no name yet"}</Pill>}
            >
              <Field label="Called" required htmlFor={`m-${i}-label`}>
                <Input
                  id={`m-${i}-label`}
                  value={master.label}
                  placeholder="Player"
                  onChange={(e) =>
                    replace(i, {
                      label: e.target.value,
                      code:
                        master.code === toCode(master.label) ? toCode(e.target.value) : master.code,
                    })
                  }
                />
              </Field>

              <Field
                label="What it is"
                hint={KINDS.find((k) => k.value === kind)?.note}
                htmlFor={`m-${i}-kind`}
              >
                <Select
                  id={`m-${i}-kind`}
                  value={kind}
                  onChange={(e) =>
                    replace(i, {
                      ...emptyMaster(e.target.value as BindingKind, master.label),
                      code: master.code,
                      fields: master.fields,
                    })
                  }
                >
                  {KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </Select>
              </Field>

              {kind === "participant" && (
                <Field label="Kind of party" htmlFor={`m-${i}-party`}>
                  <Select
                    id={`m-${i}-party`}
                    value={master.binding.participantType ?? "TEAM"}
                    onChange={(e) =>
                      replace(i, {
                        binding: { ...master.binding, participantType: e.target.value },
                      })
                    }
                  >
                    {PARTY_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0) + t.slice(1).toLowerCase()}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {kind === "competition-node" && (
                <>
                  <Names
                    id={`m-${i}-holds`}
                    label="Holds"
                    hint="Levels that may sit inside, by name"
                    placeholder={levels.map((l) => l.code).join(", ")}
                    values={master.binding.holds ?? []}
                    onChange={(holds) => replace(i, { binding: { ...master.binding, holds } })}
                  />
                  {/* On the control line, so they sit level with the inputs
                      beside them rather than above — see `CheckboxField`. */}
                  <CheckboxField
                    label="Can stand on its own"
                    checked={master.binding.canBeTop ?? true}
                    onChange={(e) =>
                      replace(i, {
                        binding: { ...master.binding, canBeTop: e.target.checked },
                      })
                    }
                  />
                  <CheckboxField
                    label="Fixtures can hang here"
                    checked={master.binding.hasFixtures ?? false}
                    onChange={(e) =>
                      replace(i, {
                        binding: { ...master.binding, hasFixtures: e.target.checked },
                      })
                    }
                  />
                </>
              )}

              <div style={{ gridColumn: "1 / -1" }}>
                <Disclosure
                  title="What you record about each one"
                  note="A player has a date of birth and a batting hand; a venue has a city. Every box added here becomes a column on that screen and a column in its import sheet."
                  count={(master.fields ?? []).length}
                >
                  <Fields
                    what="detail"
                    fields={master.fields ?? []}
                    masters={masters.map((m) => ({ code: m.code, label: m.label || m.code }))}
                    onChange={(fields) => replace(i, { fields })}
                  />
                </Disclosure>
              </div>
            </Row>
          );
        })}
      </Rows>
    </>
  );

  if (bare) return contents;

  return (
    <Section
      title="What it keeps"
      note="In the order you add them — that is the order they appear on every screen this sport generates."
    >
      {contents}
    </Section>
  );
}
